// frontend/src/features/dashboard/DashboardTasks.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Text,
  Group,
  Stack,
  Button,
  Badge,
  Loader,
  Alert,
  Center,
  Modal,
  ScrollArea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconCheck, IconClock, IconPlus } from '@tabler/icons-react';
import { isAfter, isSameDay, parseISO, format } from 'date-fns';
import {
  useTaskInstances,
  useCompleteTask,
  useCreateTaskDefinition,
  useTaskDefinitions,
  useUpdateTaskDefinition,
  usePauseTaskDefinition,
  useResumeTaskDefinition,
} from '@/hooks/useTasks';
import { useIsMobile } from '@/hooks/useIsMobile';
import { TaskDefinitionForm } from '@/features/tasks/TaskDefinitionForm';
import { CompleteTaskModal } from '@/features/tasks/CompleteTaskModal';
import type { TaskInstance, TaskDefinition, TaskDefinitionCreate, CompleteTaskData } from '@/types/task';
import { TASK_TYPE_LABELS, TASK_TYPE_COLORS } from '@/types/task';

const MAX_DISPLAYED_TASKS = 3;

interface TaskItemProps {
  instance: TaskInstance;
  onComplete: (instance: TaskInstance) => void;
  isMobile: boolean;
}

function TaskItem({ instance, onComplete, isMobile }: TaskItemProps) {
  const dueDate = parseISO(instance.due_at);
  const now = new Date();
  const isOverdue = isAfter(now, dueDate) && !isSameDay(now, dueDate);
  const isDueToday = isSameDay(now, dueDate);

  const taskTypeLabel = instance.task_type
    ? TASK_TYPE_LABELS[instance.task_type]
    : 'Task';
  const taskTypeColor = instance.task_type
    ? TASK_TYPE_COLORS[instance.task_type]
    : 'gray';

  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs">
          <Text size="sm" fw={500} truncate>
            {instance.name}
          </Text>
          <Badge size="xs" color={taskTypeColor} variant="light">
            {taskTypeLabel}
          </Badge>
        </Group>
        <Group gap="xs">
          <IconClock size={12} style={{ opacity: 0.6 }} />
          <Text size="xs" c="dimmed">
            {format(dueDate, 'MMM d')}
          </Text>
          {isOverdue && (
            <Badge size="xs" color="red" variant="filled">
              Overdue
            </Badge>
          )}
          {isDueToday && !isOverdue && (
            <Badge size="xs" color="orange" variant="filled">
              Due Today
            </Badge>
          )}
          {instance.recurrence_limit != null && instance.completion_count != null && (
            <Badge size="xs" color="violet" variant="light">
              {instance.completion_count}/{instance.recurrence_limit}
            </Badge>
          )}
        </Group>
      </Stack>
      <Button
        size="xs"
        variant="light"
        color="green"
        leftSection={<IconCheck size={isMobile ? 12 : 14} />}
        onClick={() => onComplete(instance)}
      >
        Done
      </Button>
    </Group>
  );
}

export function DashboardTasks() {
  const { data, isLoading, isError } = useTaskInstances();
  const completeTask = useCompleteTask();
  const createMutation = useCreateTaskDefinition();
  const { data: definitionsData } = useTaskDefinitions();
  const updateMutation = useUpdateTaskDefinition();
  const pauseMutation = usePauseTaskDefinition();
  const resumeMutation = useResumeTaskDefinition();
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const [completeModalInstance, setCompleteModalInstance] = useState<TaskInstance | null>(null);
  const [editingDefinition, setEditingDefinition] = useState<TaskDefinition | null>(null);
  const isMobile = useIsMobile();

  const handleCreateSubmit = (formData: TaskDefinitionCreate) => {
    createMutation.mutate(formData, { onSuccess: () => closeForm() });
  };

  const handleEditSubmit = (formData: TaskDefinitionCreate, desiredStatus?: 'active' | 'paused') => {
    if (editingDefinition) {
      updateMutation.mutate(
        { id: editingDefinition.id, data: formData },
        {
          onSuccess: () => {
            if (desiredStatus && desiredStatus !== editingDefinition.status) {
              if (desiredStatus === 'paused') {
                pauseMutation.mutate(editingDefinition.id);
              } else {
                resumeMutation.mutate(editingDefinition.id);
              }
            }
            setEditingDefinition(null);
          },
        }
      );
    }
  };

  const handleComplete = (instance: TaskInstance) => {
    setCompleteModalInstance(instance);
  };

  const handleCompleteSubmit = (data: CompleteTaskData) => {
    if (completeModalInstance) {
      completeTask.mutate(
        { instanceId: completeModalInstance.id, data },
        { onSuccess: () => setCompleteModalInstance(null) },
      );
    }
  };

  const instances = data?.instances ?? [];
  const total = data?.total ?? 0;
  const displayedTasks = instances.slice(0, MAX_DISPLAYED_TASKS);
  const remainingCount = total - MAX_DISPLAYED_TASKS;

  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} size="lg">
            Tasks
          </Text>
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={isMobile ? 12 : 14} />}
              onClick={openForm}
            >
              {isMobile ? 'New' : 'Create Task'}
            </Button>
            <Button
              component={Link}
              to="/tasks?tab=schedule"
              variant="subtle"
              size="xs"
            >
              View All
            </Button>
          </Group>
        </Group>

        {isLoading && (
          <Center py="md" data-testid="dashboard-tasks-loading">
            <Loader size="sm" />
          </Center>
        )}

        {isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            Failed to load tasks
          </Alert>
        )}

        {!isLoading && !isError && instances.length === 0 && (
          <Text c="dimmed" size="sm" ta="center" py="md">
            No tasks due
          </Text>
        )}

        {!isLoading && !isError && instances.length > 0 && (
          <Stack gap="sm">
            {displayedTasks.map((instance) => (
              <TaskItem
                key={instance.id}
                instance={instance}
                onComplete={handleComplete}
                isMobile={isMobile}
              />
            ))}

            {remainingCount > 0 && (
              <Text
                component={Link}
                to="/tasks?tab=schedule"
                size="sm"
                c="blue"
                ta="center"
                style={{ textDecoration: 'none' }}
              >
                +{remainingCount} more tasks
              </Text>
            )}
          </Stack>
        )}
      </Stack>

      <Modal
        opened={formOpened || !!editingDefinition}
        onClose={() => {
          closeForm();
          setEditingDefinition(null);
        }}
        title={editingDefinition ? 'Edit Task' : 'Create Task'}
        size="lg"
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <TaskDefinitionForm
          definition={editingDefinition || undefined}
          onSubmit={editingDefinition ? handleEditSubmit : handleCreateSubmit}
          onCancel={() => {
            closeForm();
            setEditingDefinition(null);
          }}
          isLoading={editingDefinition ? updateMutation.isPending : createMutation.isPending}
        />
      </Modal>

      <CompleteTaskModal
        opened={!!completeModalInstance}
        onClose={() => setCompleteModalInstance(null)}
        instance={completeModalInstance}
        onComplete={handleCompleteSubmit}
        onEditTask={() => {
          if (completeModalInstance) {
            const definition = definitionsData?.definitions.find(
              (d) => d.id === completeModalInstance.definition_id
            );
            if (definition) {
              setCompleteModalInstance(null);
              setEditingDefinition(definition);
            }
          }
        }}
        isLoading={completeTask.isPending}
      />
    </Card>
  );
}
